import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';

import { ActivatedRoute } from '@angular/router';
import { AlertsType } from '@rappi/ui/alerts';
import { AccountsService } from '../../../../services/accounts.service';

import { Account, Config } from '../../../../definitions/accounts.models';
import { Permission } from '../../../../definitions/permissions.models';
import { AccountStatus, AccountStatusColor, AccountStatusMessage } from '../../../../definitions/permissions.enums';
import { AlertsService } from '../../../../..//services/alerts/alerts.service';

import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../store/states/app.state';
import { StoreName } from '../../../../../store/definitions/store.constants';
import { CountryState } from '../../../../../store/states/country.state';
import { COUNTRY_NAME } from '../../../../../definitions/countries.constants';
import { catchError, map, skip, take, takeUntil, tap, switchMap } from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, Subject, ReplaySubject } from 'rxjs';
import { ACTIONS } from '../../../../definitions/actions.enums';
import { MakersFinancesMgrService } from '../../../../../financial-hub/services/makers-finances-mgr.service';
import { CompanyListFilters } from '../../../../../financial-hub/models/makers-finances-mgr.models';

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ViewComponent implements OnInit, OnDestroy {
  id: number;
  data$: Observable<Account>;
  country$: Observable<string>;
  VIEW = ACTIONS.view;
  showStoreAssigmentAccount = false;
  showMakerAssigmentAccount = false;
  planLabel: string;
  consumeCompaniesFromFinances = false;
  company$: Observable<unknown>;

  deleteAccount: (id: number) => Observable<Permission<unknown>>;

  readonly ACCOUNT_STATUS = AccountStatus;
  readonly ACCOUNT_STATUS_COLOR = AccountStatusColor;
  readonly ACCOUNT_STATUS_MESSAGE = AccountStatusMessage;

  destroySubject$: Subject<void> = new Subject();
  // Emits once the consume-companies-from-finances flag has loaded. getCompany gates on this so
  // it never dispatches before the flag is known — otherwise navigating list→view could fire Zoho
  // (default false) before the flag resolves, even when the flag should be on.
  private readonly featureFlagsLoaded$ = new ReplaySubject<void>(1);

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _accountsService: AccountsService,
    private readonly _alertsService: AlertsService,
    private readonly _location: Location,
    private readonly _store: Store<AppState>,
    private readonly _financesService: MakersFinancesMgrService
  ) {
    this.id = Number(this._activatedRoute.snapshot.params.id);
    this.deleteAccount = (id: number) => this._accountsService.deleteAccount(id).pipe(tap(() => this._location.back()));

    this.country$ = this._store.pipe(
      select(StoreName.country),
      map(({ countrySelected }: CountryState): string => COUNTRY_NAME[countrySelected])
    );
    this.country$
      .pipe(
        skip(1),
        takeUntil(this.destroySubject$),
        tap(() => this._location.back())
      )
      .subscribe();
  }

  ngOnInit() {
    this.getFeatureFlags();
    this.getConfig();

    this.data$ = this._accountsService.getAccount(this.id).pipe(
      map(({ data }: Permission<Account>): Account => data),
      catchError(({ error: { message } }: HttpErrorResponse) => {
        this._alertsService.openAlerts(message || 'There was an error trying to get account', AlertsType.error);
        return EMPTY;
      })
    );

    this.data$.subscribe(value=>{
      this.showMakerAssigmentAccount = (Object.keys(value.statement).length > 0);
      this.showStoreAssigmentAccount = (Object.keys(value.store_statement).length > 0);
      if(value.company_id){
        this.company$ = this.getCompany(value.company_id);
      }
    });
  }

  getFeatureFlags(): void {
    const countries$ = this._store.pipe(
      select(StoreName.country),
      take(1),
      map(data => data.countrySelected.toLowerCase())
    );
    const featureFlags$ = this._accountsService.getFeatureFlags();
    forkJoin([countries$, featureFlags$]).subscribe(
      ([country, featureFlags]) => {
        this.consumeCompaniesFromFinances = featureFlags['one-frontend']?.['consume-companies-from-finances']?.[country];
        this.featureFlagsLoaded$.next();
      },
      // On flag-load failure, unblock getCompany with the default (Zoho) so the view isn't left
      // without a company name forever.
      () => this.featureFlagsLoaded$.next()
    );
  }

  // Flag-aware company lookup for the view. Gated on featureFlagsLoaded$ so it waits for the flag
  // before choosing the service. When the flag is on, uses makers-finances-mgr with `{ q: makerCode }`
  // (no statuses filter) and maps the record to the shape the template expects ({ data: [{ Name }] }).
  // When off/null, uses the legacy Zoho endpoint.
  getCompany(search: string) {
    return this.featureFlagsLoaded$.pipe(
      take(1),
      switchMap(() => this.consumeCompaniesFromFinances
        ? this._financesService.getCompanies({ q: search } as CompanyListFilters, 1).pipe(
            map(resp => ({ data: [{ Name: resp.data.records[0]?.name ?? '' }] }))
          )
        : this._accountsService.getCompanies(1, 1, search, false))
    );
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  getConfig(): void {
    this._accountsService
      .getConfig()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((res: Config) => (this.planLabel = res.account?.plans_label ?? ''));
  }
}
